import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { adminLegislatorBrief, adminLegislatorBriefPdfUrl, adminLegislatorBriefRefresh } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft, Download, Landmark, Users as UsersIcon, Zap,
  Vote as VoteIcon, MessageSquare, Sparkles, Info, Printer, RefreshCw,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const URGENCY_STYLES = {
  high:   "bg-red-100 text-red-800 border-red-200",
  medium: "bg-amber-100 text-amber-800 border-amber-200",
  low:    "bg-emerald-100 text-emerald-800 border-emerald-200",
};

function ScoreBar({ score, matches, total, overridden }) {
  if (score === null || score === undefined) {
    return <div className="text-sm text-stone-500 italic">No votes recorded yet — alignment score unavailable.</div>;
  }
  const color = score >= 75 ? "bg-emerald-500"
             : score >= 50 ? "bg-amber-500"
             :               "bg-red-500";
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs uppercase tracking-widest text-ccc-purple">CCC alignment</div>
        <div className="text-sm text-stone-700 font-medium">
          <span className="font-mono text-lg" data-testid="brief-score">{score}%</span>
          <span className="text-xs text-stone-500 ml-2">
            {matches ?? 0}/{total ?? 0} tracked votes
          </span>
          {overridden && <span className="ml-2 text-[10px] uppercase tracking-widest text-stone-500">manual</span>}
        </div>
      </div>
      <div className="h-2 bg-stone-100 relative overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${score}%` }}></div>
      </div>
    </div>
  );
}

export default function LegislatorBrief() {
  const { id } = useParams();
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setBrief(await adminLegislatorBrief(id)); }
    catch { toast.error("Could not load brief"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const refreshAI = async () => {
    setRefreshing(true);
    try {
      const b = await adminLegislatorBriefRefresh(id);
      setBrief(b);
      toast.success(`Refreshed — ${b.top_concerns?.length || 0} concerns from the AI assistant.`);
    } catch { toast.error("Refresh failed"); }
    finally { setRefreshing(false); }
  };

  if (loading) {
    return <div className="p-16 text-stone-500 text-sm text-center" data-testid="brief-loading">
      Building brief… (this can take 3-5 seconds while the AI summarises district concerns)
    </div>;
  }
  if (!brief) return <div className="p-8 text-stone-500 text-sm">Brief unavailable.</div>;

  const leg = brief.legislator;
  const sc = leg.scorecard;
  const dc = brief.district_contacts;
  const actions = brief.actions_this_quarter;

  return (
    <div data-testid="constituent-brief-page">
      <div className="mb-4 flex items-center justify-between gap-4 flex-wrap">
        <Link to="/admin/legislators" className="inline-flex items-center gap-1 text-xs text-ccc-purple hover:underline">
          <ArrowLeft size={12}/> Back to legislators
        </Link>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => window.print()} data-testid="btn-print-brief">
            <Printer size={14} className="mr-1.5"/>Print
          </Button>
          <a
            href={adminLegislatorBriefPdfUrl(id)}
            target="_blank" rel="noreferrer"
            data-testid="btn-download-brief"
          >
            <Button size="sm">
              <Download size={14} className="mr-1.5"/>Download PDF brief
            </Button>
          </a>
        </div>
      </div>

      {/* Legislator header */}
      <div className="bg-white border border-stone-200 p-6 mb-6">
        <div className="flex items-start gap-6 flex-wrap">
          {leg.photo_url ? (
            <img src={leg.photo_url} alt={leg.name} className="w-20 h-20 rounded-full object-cover"/>
          ) : (
            <div className="w-20 h-20 rounded-full bg-ccc-plum/10 text-ccc-plum flex items-center justify-center">
              <Landmark size={32}/>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-[0.3em] text-ccc-gold-dark mb-1">Constituent match brief</div>
            <h1 className="font-serif text-4xl text-ccc-plum" data-testid="brief-legislator-name">{leg.name}</h1>
            <div className="text-sm text-stone-600 mt-1">
              {leg.chamber} · {leg.district}{leg.party && ` · ${leg.party}`}
              {leg.committees?.length ? ` · ${leg.committees.slice(0,3).join(", ")}` : ""}
            </div>
            {leg.email && <div className="text-xs text-stone-500 mt-1">{leg.email}{leg.phone && ` · ${leg.phone}`}</div>}
          </div>
          <div className="w-full md:w-80">
            <ScoreBar
              score={sc?.displayed_score}
              matches={sc?.matches}
              total={sc?.total}
              overridden={sc?.is_overridden}
            />
          </div>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-stone-200 p-5" data-testid="brief-kpi-contacts">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-ccc-purple mb-1">
            <UsersIcon size={12}/>District contacts
          </div>
          <div className="font-serif text-3xl text-ccc-plum">{dc.total}</div>
          <div className="text-xs text-stone-500">in {leg.district} · <Zap size={11} className="inline mr-0.5"/>{dc.total_engagement} total engagement</div>
        </div>
        <div className="bg-white border border-stone-200 p-5" data-testid="brief-kpi-actions">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-ccc-purple mb-1">
            <VoteIcon size={12}/>Actions taken (90d)
          </div>
          <div className="font-serif text-3xl text-ccc-plum">{actions.total}</div>
          <div className="text-xs text-stone-500">from constituents · to this legislator</div>
        </div>
        <div className="bg-white border border-stone-200 p-5" data-testid="brief-kpi-concerns">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-ccc-purple mb-1">
            <Sparkles size={12}/>AI insights
          </div>
          <div className="font-serif text-3xl text-ccc-plum">{brief.top_concerns?.length || 0}</div>
          <div className="text-xs text-stone-500">themes from AI assistant · past 90 days</div>
        </div>
      </div>

      {/* Top 3 concerns */}
      <section className="bg-white border border-stone-200 p-6 mb-6" data-testid="brief-concerns-section">
        <div className="flex items-start justify-between mb-1 gap-4 flex-wrap">
          <div>
            <h2 className="font-serif text-2xl text-ccc-plum">Top constituent concerns</h2>
            <div className="text-xs text-stone-500">
              Clustered from AI-assistant conversations of contacts in {leg.district}. Cached 15 min.
            </div>
          </div>
          <Button
            size="sm" variant="outline" onClick={refreshAI} disabled={refreshing}
            data-testid="btn-refresh-ai-concerns"
          >
            <RefreshCw size={12} className={`mr-1.5 ${refreshing ? "animate-spin" : ""}`}/>
            {refreshing ? "Regenerating…" : "Refresh AI"}
          </Button>
        </div>
        <div className="mt-4">
        {brief.top_concerns?.length ? (
          <div className="grid sm:grid-cols-3 gap-3">
            {brief.top_concerns.map((cn, i) => (
              <div key={i} className="border border-stone-200 p-4" data-testid={`brief-concern-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs text-stone-400">#{i+1}</div>
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${URGENCY_STYLES[cn.urgency] || URGENCY_STYLES.medium}`}>
                    {cn.urgency || "medium"}
                  </span>
                </div>
                <div className="font-serif text-lg text-ccc-plum leading-tight">{cn.label}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-stone-500 italic flex items-center gap-2">
            <Info size={14}/>
            Not enough AI-assistant data from this district yet — check back after more constituents chat.
          </div>
        )}
        </div>
      </section>

      {/* District top engaged */}
      <section className="bg-white border border-stone-200 p-6 mb-6" data-testid="brief-top-engaged">
        <h2 className="font-serif text-2xl text-ccc-plum mb-1">Most engaged constituents</h2>
        <div className="text-xs text-stone-500 mb-4">Top 10 by engagement score — click to open Contact 360.</div>
        {dc.top_engaged?.length ? (
          <div className="divide-y divide-stone-100">
            {dc.top_engaged.map(c => (
              <Link
                to={`/admin/contacts/${c.id}`}
                key={c.id}
                className="flex items-center gap-3 py-2 hover:bg-stone-50"
                data-testid={`brief-contact-${c.id}`}
              >
                <div className="w-8 h-8 rounded-full bg-ccc-plum/10 text-ccc-plum text-xs flex items-center justify-center">
                  {(c.name || "?").slice(0,1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-stone-800 truncate">{c.name || <em>Unnamed</em>}</div>
                  <div className="text-xs text-stone-500 truncate">
                    {c.email} {c.parish ? ` · ${c.parish}` : ""}
                  </div>
                </div>
                <div className="text-sm font-mono text-ccc-purple">
                  <Zap size={12} className="inline mr-0.5"/>{c.engagement_score ?? 0}
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-sm text-stone-500 italic">No contacts in {leg.district} yet.</div>
        )}
      </section>

      {/* Actions this quarter */}
      <section className="bg-white border border-stone-200 p-6 mb-6" data-testid="brief-actions-section">
        <h2 className="font-serif text-2xl text-ccc-plum mb-1">Actions this quarter</h2>
        <div className="text-xs text-stone-500 mb-4">Past 90 days · constituent emails routed through CCC to this legislator.</div>
        {actions.recent?.length ? (
          <div className="divide-y divide-stone-100">
            {actions.recent.map(a => (
              <div key={a.id} className="py-2 flex items-center gap-3">
                <MessageSquare size={14} className="text-ccc-purple flex-shrink-0"/>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-stone-800 truncate">{a.subject || a.alert_title || "Action taken"}</div>
                  <div className="text-xs text-stone-500">
                    {a.alert_title && a.subject !== a.alert_title ? `${a.alert_title} · ` : ""}
                    {formatDistanceToNow(new Date(a.created_at), { addSuffix: true })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-stone-500 italic">No actions taken this quarter.</div>
        )}
      </section>

      {/* Talking points */}
      <section className="bg-white border border-stone-200 p-6 mb-6" data-testid="brief-talking-points">
        <h2 className="font-serif text-2xl text-ccc-plum mb-1">Suggested talking points</h2>
        <div className="text-xs text-stone-500 mb-4">Ready-to-cite CCC positions on the bills this legislator has voted on.</div>
        {brief.talking_points?.length ? (
          <ul className="space-y-2 text-sm text-stone-800 list-disc pl-5">
            {brief.talking_points.map((tp, i) => <li key={i} data-testid={`brief-talking-point-${i}`}>{tp}</li>)}
          </ul>
        ) : (
          <div className="text-sm text-stone-500 italic">Record votes on CCC-tracked bills to seed talking points.</div>
        )}
      </section>

      <div className="text-xs text-stone-400 italic mt-4">
        Confidential lobbying brief · contains constituent PII · do not distribute outside CCC staff.
      </div>
    </div>
  );
}
